<template>
  <div class="profile-container">
    <!-- 个人中心标题 -->
    <div class="profile-header">
      <h2 class="profile-title">个人中心</h2>
    </div>

    <!-- 标签页导航 -->
    <div class="profile-tabs">
      <div 
        class="tab-item" 
        :class="{ active: activeTab === 'basic' }"
        @click="activeTab = 'basic'"
      >
        <span class="tab-icon">👤</span>
        <span class="tab-text">基本信息</span>
      </div>
      <div 
        class="tab-item" 
        :class="{ active: activeTab === 'subscription' }"
        @click="handleSubscriptionTabClick"
      >
        <span class="tab-icon">📋</span>
        <span class="tab-text">订阅套餐</span>
      </div>
      <div 
        class="tab-item" 
        :class="{ active: activeTab === 'exchange' }"
        @click="handleExchangeTabClick"
      >
        <span class="tab-icon">🎫</span>
        <span class="tab-text">兑换记录</span>
      </div>
    </div>

    <!-- 标签页内容 -->
    <div class="profile-content">
      
      <!-- 基本信息 -->
      <div v-if="activeTab === 'basic'" class="tab-content">
        <div class="content-section basic-info">
          <div class="info-grid">
            <div class="info-item">
              <label class="info-label">
                <span class="label-icon">👤</span>
                用户名: {{ userInfo.userToken || '未知' }}
              </label>
            </div>
            
            <div class="info-item">
              <label class="info-label">
                <span class="label-icon">📧</span>
                邮箱: {{ userInfo.email || '未绑定' }}
              </label>
            </div>
          </div>

          <!-- 产品过期时间 -->
          <div class="expiry-section">
            <h4>产品过期时间</h4>
            <div class="expiry-grid">
              <div class="expiry-item">
                <div class="expiry-row">
                  <div class="expiry-label">
                    <span class="expiry-icon">🤖</span>
                    <span class="expiry-service">ChatGPT:</span>
                  </div>
                  <div class="expiry-value" :class="{ 'expired': isExpired(userInfo.expireTime) }">
                    {{ formatExpireTime(userInfo.expireTime) }}
                  </div>
                </div>
              </div>

              <div class="expiry-item">
                <div class="expiry-row">
                  <div class="expiry-label">
                    <span class="expiry-icon">⚡</span>
                    <span class="expiry-service">Grok:</span>
                  </div>
                  <div class="expiry-value" :class="{ 'expired': isExpired(userInfo.grokExpireTime) }">
                    {{ formatExpireTime(userInfo.grokExpireTime) }}
                  </div>
                </div>
              </div>

              <div class="expiry-item">
                <div class="expiry-row">
                  <div class="expiry-label">
                    <span class="expiry-icon">✨</span>
                    <span class="expiry-service">Gemini:</span>
                  </div>
                  <div class="expiry-value" :class="{ 'expired': isExpired(userInfo.geminiExpireTime) }">
                    {{ formatExpireTime(userInfo.geminiExpireTime) }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 账号安全 -->
          <div class="security-section">
            <h4>账号安全</h4>
            <div class="security-actions">
              <button class="tech-btn info" @click="$emit('back')">
                返回车队
              </button>
              <button class="tech-btn primary" @click="$emit('change-password')">
                修改密码
              </button>
              <button class="tech-btn danger" @click="$emit('logout')">
                退出登录
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 订阅套餐 -->
      <div v-if="activeTab === 'subscription'" class="tab-content">
        <div class="content-section subscription-info">
          <!-- 加载状态 -->
          <div v-if="subscriptionsLoading" class="loading-state">
            <div class="loading-spinner">⏳</div>
            <p>正在加载订阅套餐...</p>
          </div>
          
          <!-- 错误状态 -->
          <div v-else-if="subscriptionsError" class="error-state">
            <div class="error-icon">❌</div>
            <p class="error-message">{{ subscriptionsError }}</p>
            <button class="retry-btn" @click="loadSubscriptions">重试</button>
          </div>
          
          <!-- 订阅套餐表格 -->
          <div v-else-if="subscriptions.length > 0" class="subscriptions-table-container">
            <div class="subscriptions-table">
              <!-- 表格头部 -->
              <div class="table-header">
                <div class="table-cell">服务类型</div>
                <div class="table-cell">套餐名称</div>
                <div class="table-cell">开始时间</div>
                <div class="table-cell">到期时间</div>
                <div class="table-cell">状态</div>
              </div>
              
              <!-- 表格内容 -->
              <div v-for="sub in subscriptions" :key="sub.id" class="table-row">
                <div class="table-cell service-type" data-label="服务类型">
                  <span class="service-name-bold">
                    {{ sub.subType.charAt(0).toUpperCase() + sub.subType.slice(1) }}
                  </span>
                </div>
                <div class="table-cell" data-label="套餐名称">{{ sub.subTypeName }}</div>
                <div class="table-cell" data-label="开始时间">{{ formatDateTime(sub.startTime) }}</div>
                <div class="table-cell" data-label="到期时间" :class="{ 'expired-date': isExpired(sub.expireTime) }">
                  {{ formatDateTime(sub.expireTime) }}
                </div>
                <div class="table-cell" data-label="状态">
                  <span class="status-badge" :class="getStatusClass(sub.status)">
                    {{ getStatusText(sub.status) }}
                  </span>
                </div>
              </div>
            </div>
            
            <!-- 分页信息 -->
            <div class="table-footer">
              <span class="total-info">共 {{ subscriptions.length }} 条</span>
            </div>
          </div>
          
          <!-- 无数据 -->
          <div v-else class="no-data">
            <div class="no-data-icon">📋</div>
            <p>暂无订阅套餐信息</p>
          </div>
        </div>
      </div>

      <!-- 兑换记录 -->
      <div v-if="activeTab === 'exchange'" class="tab-content">
        <div class="content-section exchange-records">
          <!-- 加载状态 -->
          <div v-if="redemptionLoading" class="loading-state">
            <div class="loading-spinner">⏳</div>
            <p>正在加载兑换记录...</p>
          </div>
          
          <!-- 错误状态 -->
          <div v-else-if="redemptionError" class="error-state">
            <div class="error-icon">❌</div>
            <p class="error-message">{{ redemptionError }}</p>
            <button class="retry-btn" @click="loadRedemptions">重试</button>
          </div>
          
          <!-- 兑换记录表格 -->
          <div v-else-if="redemptionList.length > 0" class="redemption-table-container">
            <div class="redemption-table">
              <!-- 表格头部 -->
              <div class="table-header">
                <div class="table-cell">兑换码</div>
                <div class="table-cell">兑换时间</div>
                <div class="table-cell">备注</div>
              </div>
              
              <!-- 表格内容 -->
              <div v-for="redemption in redemptionList" :key="redemption.id" class="table-row">
                <div class="table-cell redemption-code" data-label="兑换码">
                  <span class="code-text">{{ redemption.redemptionKey }}</span>
                </div>
                <div class="table-cell" data-label="兑换时间">
                  {{ formatDateTime(redemption.createTime) }}
                </div>
                <div class="table-cell" data-label="备注">
                  {{ redemption.remark || '-' }}
                </div>
              </div>
            </div>
            
            <!-- 分页信息 -->
            <div class="table-footer">
              <span class="total-info">共 {{ redemptionList.length }} 条兑换记录</span>
            </div>
          </div>
          
          <!-- 无数据 -->
          <div v-else class="no-data">
            <div class="no-data-icon">🎫</div>
            <p>暂无兑换记录</p>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { getUserSubscriptions, getRedemptionList } from '../api/home'

defineProps({
  userInfo: {
    type: Object,
    default: () => ({})
  }
})

defineEmits(['back', 'change-password', 'logout'])

const activeTab = ref('basic')
const subscriptions = ref([])
const subscriptionsLoading = ref(false)
const subscriptionsError = ref('')
const redemptionList = ref([])
const redemptionLoading = ref(false)
const redemptionError = ref('')

const isExpired = (expireTime) => {
  if (!expireTime) return false
  return new Date(expireTime) < new Date()
}

const formatDateTime = (dateTime) => {
  if (!dateTime) return '-'
  return dateTime.replace(' ', ' ')
}

// 格式化过期时间显示（只显示到小时）
const formatExpireTime = (expireTime) => {
  if (!expireTime || expireTime === '未知') return '无此产品订阅'

  try {
    // 如果已经是格式化的时间（2025-10-16 20时），直接返回
    if (expireTime.includes('时') && !expireTime.includes(':')) {
      return expireTime
    }

    // 解析时间字符串，提取日期和小时部分
    const date = new Date(expireTime)
    if (isNaN(date.getTime())) {
      return expireTime // 如果解析失败，返回原值
    }

    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hour = date.getHours()

    return `${year}-${month}-${day} ${hour}时`
  } catch (error) {
    console.error('格式化过期时间失败:', error)
    return expireTime // 出错时返回原值
  }
}

const getStatusClass = (status) => {
  if (status === 1) return 'status-active'
  if (status === 2) return 'status-expired'
  if (status === 0) return 'status-pending'
  return 'status-unknown'
}

const getStatusText = (status) => {
  if (status === 1) return '生效中'
  if (status === 2) return '过期'
  if (status === 0) return '待生效'
  return '未知'
}

const loadSubscriptions = async () => {
  if (subscriptionsLoading.value) return
  
  subscriptionsLoading.value = true
  subscriptionsError.value = ''
  
  try {
    const response = await getUserSubscriptions()
    
    if (response.code === 1) {
      subscriptions.value = response.data.list || []
    } else {
      subscriptionsError.value = response.msg || '获取订阅套餐失败'
    }
  } catch (error) {
    subscriptionsError.value = `网络错误: ${error.message}`
  } finally {
    subscriptionsLoading.value = false
  }
}

const loadRedemptions = async () => {
  if (redemptionLoading.value) return
  
  redemptionLoading.value = true
  redemptionError.value = ''
  
  try {
    const response = await getRedemptionList()
    
    if (response.code === 1) {
      redemptionList.value = response.data.list || []
    } else {
      redemptionError.value = response.msg || '获取兑换记录失败'
    }
  } catch (error) {
    redemptionError.value = `网络错误: ${error.message}`
  } finally {
    redemptionLoading.value = false
  }
}

const handleSubscriptionTabClick = () => {
  activeTab.value = 'subscription'
  if (subscriptions.value.length === 0) {
    loadSubscriptions()
  }
}

const handleExchangeTabClick = () => {
  activeTab.value = 'exchange'
  if (redemptionList.value.length === 0) {
    loadRedemptions()
  }
}

onMounted(() => {
  // 如果需要，可以在挂载时加载数据
})
</script>

<style scoped>
.profile-container {
  background: #fff;
  border: 2px solid rgba(0,0,0,.05);
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0,0,0,.05);
}

.profile-header {
  margin-bottom: 24px;
}

.profile-title {
  font-size: 24px;
  font-weight: 900;
  color: #1f1f23;
  margin: 0;
}

.profile-tabs {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  border-bottom: 2px solid #ececf0;
  padding-bottom: 0;
}

.tab-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 12px 12px 0 0;
  background: transparent;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 600;
  color: #666;
  border-bottom: 3px solid transparent;
  flex: 1;
  justify-content: center;
  min-width: 0;
}

.tab-item:hover {
  background: #f6f4ff;
  color: #7b5cff;
}

.tab-item.active {
  background: linear-gradient(141deg, rgba(191,102,255,1) 0%, rgba(98,72,255,1) 50%, rgba(0,221,235,1) 100%);
  color: #fff;
  border-bottom-color: #7b5cff;
}

.tab-icon {
  font-size: 18px;
}

.tab-text {
  font-size: 14px;
}

.profile-content {
  min-height: 300px;
}

.tab-content {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.content-section {
  padding: 20px;
}

.basic-info .info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.info-item {
  background: #f5f5f7;
  padding: 16px;
  border-radius: 12px;
}

.info-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
  font-weight: 600;
}

.label-icon {
  font-size: 18px;
}

.expiry-section {
  margin-bottom: 24px;
}

.expiry-section h4 {
  font-size: 16px;
  font-weight: 700;
  color: #333;
  margin: 0 0 16px 0;
}

.expiry-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.expiry-item {
  background: #f5f5f7;
  padding: 12px 16px;
  border-radius: 8px;
}

.expiry-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.expiry-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #666;
}

.expiry-icon {
  font-size: 16px;
}

.expiry-value {
  font-weight: 600;
  color: #333;
}

.expiry-value.expired {
  color: #ff4d4f;
}

.no-expiry-data {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  color: #999;
}

.no-data-icon {
  font-size: 24px;
}

.security-section h4 {
  font-size: 16px;
  font-weight: 700;
  color: #333;
  margin: 0 0 16px 0;
}

.security-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.tech-btn {
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  border: none;
}

.tech-btn.info {
  background: #f5f5f7;
  color: #666;
}

.tech-btn.info:hover {
  background: #e8e8e8;
  color: #333;
}

.tech-btn.primary {
  background: linear-gradient(135deg, #7b5cff, #9b7bff);
  color: white;
  box-shadow: 0 4px 12px rgba(123, 92, 255, 0.3);
}

.tech-btn.primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(123, 92, 255, 0.4);
}

.tech-btn.danger {
  background: #ff4d4f;
  color: white;
}

.tech-btn.danger:hover {
  background: #ff7875;
  transform: translateY(-2px);
}

.loading-state,
.error-state,
.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  text-align: center;
  color: #666;
}

.loading-spinner,
.error-icon,
.no-data-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.retry-btn {
  margin-top: 16px;
  padding: 8px 16px;
  background: #7b5cff;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
}

.retry-btn:hover {
  background: #9b7bff;
}

.subscriptions-table-container,
.redemption-table-container {
  overflow-x: auto;
  padding: 1px; /* 给滚动容器添加少量内边距 */
}

.subscriptions-table {
  width: 100%;
  min-width: 350px; /* 桌面端最小宽度 */
}

.redemption-table {
  width: 100%;
  min-width: 300px; /* 兑换码表格桌面端最小宽度 */
}

/* 订阅套餐表格列宽调整 - 桌面端 */
.subscriptions-table .table-header,
.subscriptions-table .table-row {
  grid-template-columns: 1.2fr 0.8fr 1fr 1fr 0.6fr;
}

/* 兑换码列允许换行 - 桌面端 */
.redemption-table .table-cell:first-child {
  word-break: break-all;
  overflow-wrap: break-word;
  white-space: normal;
}


.table-header,
.table-row {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  padding: 12px;
  align-items: center;
}

/* 兑换码表格列宽调整 - 桌面端 */
.redemption-table .table-header,
.redemption-table .table-row {
  grid-template-columns: 2fr 1.2fr 1.2fr;
}

.table-header {
  background: #f5f5f7;
  border-radius: 8px;
  font-weight: 700;
  color: #333;
}

.table-row {
  border-bottom: 1px solid #ececf0;
}

.table-row:hover {
  background: #f9f9f9;
}

.table-cell {
  font-size: 14px;
  color: #666;
}

.service-name-bold {
  font-weight: 700;
  color: #7b5cff;
}

.expired-date {
  color: #ff4d4f;
  font-weight: 600;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.status-active {
  background: #f6ffed;
  color: #52c41a;
}

.status-expired {
  background: #fff1f0;
  color: #ff4d4f;
}

.status-pending {
  background: #fffbe6;
  color: #faad14;
}

.table-footer {
  margin-top: 16px;
  padding: 12px;
  text-align: right;
  color: #999;
  font-size: 13px;
}

.code-text {
  font-family: 'Courier New', monospace;
  font-weight: 600;
  color: #7b5cff;
  font-size: 14px;
}

@media (max-width: 768px) {
  .profile-container {
    padding: 16px;
  }

  .profile-tabs {
    gap: 6px;
    flex-wrap: nowrap;
    overflow-x: auto;
  }

  .tab-item {
    padding: 10px 12px;
    font-size: 12px;
    gap: 4px;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .tab-icon {
    font-size: 16px;
  }

  .basic-info .info-grid {
    grid-template-columns: 1fr;
  }

  /* 平板端表格优化 */
  @media (min-width: 769px) and (max-width: 1024px) {
    .subscriptions-table,
    .redemption-table {
      min-width: 500px;
    }

    /* 平板端订阅套餐表格列宽调整 */
    .subscriptions-table .table-header,
    .subscriptions-table .table-row {
      grid-template-columns: 1.1fr 0.7fr 0.9fr 0.9fr 0.5fr;
    }

    /* 平板端兑换码表格列宽调整 */
    .redemption-table .table-header,
    .redemption-table .table-row {
      grid-template-columns: 1.8fr 1.1fr 1.1fr;
    }
  }

  .security-actions {
    flex-direction: column;
  }

  .tech-btn {
    width: 100%;
  }

  .table-header {
    display: none;
  }

  .table-row {
    display: block;
    border: 1px solid #ececf0;
    border-radius: 8px;
    margin-bottom: 12px;
    padding: 12px;
  }

  .table-cell {
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;
    min-height: 24px;
    align-items: center;
  }

  .table-cell:last-child {
    border-bottom: none;
  }

  .table-cell::before {
    content: attr(data-label);
    font-weight: 600;
    color: #333;
    flex-shrink: 0;
  }

  /* 兑换码在手机端特殊处理 */
  .redemption-table .table-cell:first-child {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 12px 0;
  }

  .redemption-table .table-cell:first-child::before {
    margin-bottom: 4px;
  }

  .redemption-table .table-cell:first-child .code-text {
    max-width: 100%;
    word-break: break-all;
    font-size: 13px;
  }

  /* 更小屏幕的特殊处理 */
  @media (max-width: 480px) {
    .table-cell {
      padding: 6px 0;
      font-size: 12px;
    }

    .code-text {
      font-size: 11px;
    }
  }
}
</style>

